'use client';

import React, { memo, useMemo } from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';
import { useBranding } from '@/contexts/BrandingContext';

interface AppLogoProps {
  src?: string;
  iconName?: string;
  size?: number;
  className?: string;
  onClick?: () => void;
  variant?: 'icon' | 'text';
}

const AppLogo = memo(function AppLogo({
  src,
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onClick,
  variant = 'icon',
}: AppLogoProps) {
  const { iconUrl, textUrl, loading } = useBranding();

  const customUrl = variant === 'text' ? textUrl : iconUrl;
  // Use custom URL if available; fall back to src prop only if explicitly provided
  const effectiveSrc = customUrl ?? src ?? null;

  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  // While loading and no cached URL available, render nothing to avoid showing stale default
  if (loading && !effectiveSrc) {
    return (
      <div
        className={containerClassName}
        style={{ width: size, height: size }}
        onClick={onClick}
      />
    );
  }

  return (
    <div className={containerClassName} onClick={onClick}>
      {effectiveSrc ? (
        <AppImage
          src={effectiveSrc}
          alt="Logo"
          width={size}
          height={size}
          className="flex-shrink-0"
          priority={true}
          unoptimized={effectiveSrc.endsWith('.svg') || effectiveSrc.includes('svg')}
        />
      ) : (
        <AppIcon name={iconName} size={size} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
